/** Guarded customer discount presentation. Never changes codes or checkout requests. */
export function discountPreviewEligible({ preview, checkoutVerified, accelerated, used }) {
  return preview === true && checkoutVerified === true && !accelerated && !used;
}

export function updateDiscountPreview(drawer, preview, checkoutVerified, message) {
  if (!drawer || !message) return;
  const accelerated = Boolean(
    drawer.querySelector('.additional-checkout-buttons, shopify-accelerated-checkout-cart')
  );
  for (const form of drawer.querySelectorAll('.cart-discount__form')) {
    const component = form.closest('cart-discount-component');
    const disclosure = form.closest('details');
    if (!disclosure) continue;
    const input = form.querySelector('input[name="discount"]');
    const used = Boolean(
      input?.value ||
      disclosure.contains(document.activeElement) ||
      component?.querySelector('.cart-discount__pill') ||
      component?.querySelector('.cart-discount__error:not(.hidden)')
    );
    const hide = discountPreviewEligible({ preview, checkoutVerified, accelerated, used });
    // Restore only changes owned by this controller; keep native state otherwise.
    let note = disclosure.nextElementSibling?.matches('[data-br-cart-discount-note]')
      ? disclosure.nextElementSibling
      : null;
    if (hide && !disclosure.hidden) {
      disclosure.hidden = true;
      disclosure.classList.add('hidden');
      disclosure.dataset.brCartDiscountHidden = '';
    } else if (!hide && 'brCartDiscountHidden' in disclosure.dataset) {
      disclosure.hidden = false;
      disclosure.classList.remove('hidden');
      delete disclosure.dataset.brCartDiscountHidden;
    }
    if (hide && !note) {
      note = document.createElement('p');
      note.dataset.brCartDiscountNote = '';
      note.className = 'cart-primary-typography';
      note.textContent = message;
      disclosure.after(note);
    } else if (!hide) {
      note?.remove();
    }
  }
}

// Customer capability bridge with optional staff modes. Its gate rechecks
// Shopify role on every invocation. No query parameter can prove checkout support.
export function installDiscountPreview({ enabled, checkoutVerified = false, message }) {
  const drawer = document.getElementById('cart-drawer');
  if (!drawer) return;
  const apply = () => updateDiscountPreview(drawer, enabled('discount'), checkoutVerified, message);
  const observer = new MutationObserver(apply);
  observer.observe(drawer, { childList: true, subtree: true });
  drawer.addEventListener('input', apply);
  const focusout = () => queueMicrotask(apply);
  drawer.addEventListener('focusout', focusout);
  apply();
  return () => {
    observer.disconnect();
    drawer.removeEventListener('input', apply);
    drawer.removeEventListener('focusout', focusout);
    updateDiscountPreview(drawer, false, false, message);
  };
}

if (typeof document !== 'undefined') {
  let uninstall;
  const bootstrap = () => {
    uninstall?.();
    uninstall = undefined;
    const helper = window.BRCartFeatures;
    const config = document.getElementById('br-cart-discount-preview-config');
    if (!helper || !config) return;
    try {
      const { message, checkoutVerified } = JSON.parse(config.textContent);
      uninstall = installDiscountPreview({
        enabled: (mode) =>
          ['main', 'unpublished'].includes(window.Shopify?.theme?.role) && helper.enabled(mode),
        checkoutVerified,
        message,
      });
    } catch {
      // Invalid/missing review configuration retains the native discount form.
    }
  };
  document.addEventListener('br:cart-review-change', bootstrap);
  bootstrap();
}
